import {Component, DestroyRef, Inject} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, ValidationErrors, Validators } from '@angular/forms';
import { CARBON_SCOPE, EMISSION_FACTOR_DESCRIPTION_MAX, EMISSION_FACTOR_NAME_MAX, EMISSION_FACTOR_NAME_MIN, EMISSION_FACTOR_TYPE, EMISSION_FACTOR_VALUE_MAX, EmissionFactor, getCarbonScopeDescription, getCarbonScopeShortDescription, getEmissionFactorDescription, getUnitsForEmissionFactor, validateEmissionFactor } from '../../common/organisation';
import { ErrorStateMatcher } from '@angular/material/core';
import { OrganisationService } from '../organisation.service';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = form && form.submitted;
      return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}

const ENERGY_INPUTS = ['density', 'energyDensity', 'energyFactor']

// creates or edits one of the organisation's emission factors; closes with a copy, which the caller saves
@Component({
    selector: 'app-emission-factor-dialog',
    templateUrl: 'emission-factor-dialog.html',
    styleUrls: ['emission-factor-dialog.css'],
    standalone: false
})
export class EmissionFactorDialogComponent {
    efForm: FormGroup
    matcher = new MyErrorStateMatcher();
    scopes = Object.values(CARBON_SCOPE)
    types = Object.values(EMISSION_FACTOR_TYPE)
    title:string
    isNew:boolean
    units:string
    scopeDescription:string
    energyTypeSelected:boolean
    EMISSION_FACTOR_NAME_MAX = EMISSION_FACTOR_NAME_MAX
    EMISSION_FACTOR_DESCRIPTION_MAX = EMISSION_FACTOR_DESCRIPTION_MAX
    saveProblem:string|undefined

    constructor(public dialogRef: MatDialogRef<EmissionFactorDialogComponent, EmissionFactor>, @Inject(MAT_DIALOG_DATA) public data: EmissionFactor, private formBuilder: FormBuilder,
        private organisationService:OrganisationService, destroyRef:DestroyRef) {
        // new factors arrive with a key already, so check whether the organisation has it
        this.isNew = !this.existingFactors.some(ef => ef._key === data._key)
        this.title = this.isNew ? "Create emission factor" : "Edit emission factor"

        this.efForm  =  this.formBuilder.group({
            name: [data.name, [Validators.required, Validators.minLength(EMISSION_FACTOR_NAME_MIN), Validators.maxLength(EMISSION_FACTOR_NAME_MAX), this.uniqueName]],
            description: [data.description, Validators.maxLength(EMISSION_FACTOR_DESCRIPTION_MAX)],
            scope: [data.scope, Validators.required],
            type: [data.type, Validators.required],
            value: [data.value, [Validators.required, Validators.min(0), Validators.max(EMISSION_FACTOR_VALUE_MAX)]],
            density: [data.density||0, [Validators.min(0), this.requiredForEnergy]],
            energyDensity: [data.energyDensity||0, [Validators.min(0), this.requiredForEnergy]],
            energyFactor: [data.energyFactor||0, [Validators.min(0), this.requiredForEnergy]]
        })

        this.efForm.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.saveProblem = undefined)

        // the energy inputs work out the factor however they change (typing, arrows or pasting)
        for (const name of ENERGY_INPUTS) {
            this.efForm.controls[name].valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => this.energyChange())
        }

        this.scopeSelected()
        this.typeSelected()
    }

    private get existingFactors():EmissionFactor[] {
        return this.organisationService.organisation?.emissionFactors || []
    }

    // two factors with the same name can't be told apart when choosing one
    private uniqueName = (control:AbstractControl):ValidationErrors | null => {
        const name = (control.value || '').trim().toLowerCase()
        const clash = name && this.existingFactors.some(ef => ef._key !== this.data._key && (ef.name || '').trim().toLowerCase() === name)
        return clash ? { duplicate: true } : null
    }

    // the energy inputs are multiplied together, so a zero would make the factor zero
    private requiredForEnergy = (control:AbstractControl):ValidationErrors | null => {
        if (!this.energyTypeSelected) return null
        return Number(control.value) > 0 ? null : { required: true }
    }

    // emissions already recorded against this factor keep their amounts, which would then be read in the new units
    getTypeChangeWarning() {
        const type = this.efForm.controls['type'].value
        if (this.isNew || !this.data.type || !type || type === this.data.type) return ''
        const from = getUnitsForEmissionFactor(this.data.type), to = getUnitsForEmissionFactor(type)
        return `Emissions already using this factor keep their amounts${from !== to ? `, which will be read as ${to || 'a total'} instead of ${from || 'a total'}` : ''}. Check them after saving.`
    }

    getNameError() {
        return `Name must be between ${EMISSION_FACTOR_NAME_MIN} and ${EMISSION_FACTOR_NAME_MAX} characters`
    }

    getDescriptionError() {
        return `Description must be ${EMISSION_FACTOR_DESCRIPTION_MAX} characters or fewer`
    }

    getValueError() {
        return `Emission factor must be between 0 and ${EMISSION_FACTOR_VALUE_MAX.toLocaleString()}`
    }

    getScopeShortDescription(scope:CARBON_SCOPE) {
        return getCarbonScopeShortDescription(scope)
    }

    getTypeName(type:EMISSION_FACTOR_TYPE) {
        return type.charAt(0).toUpperCase() + type.slice(1)
    }

    scopeSelected() {
        this.scopeDescription = getCarbonScopeDescription(this.efForm.value.scope)
    }

    typeSelected() {
        this.units = getUnitsForEmissionFactor(this.efForm.value.type)

        this.energyTypeSelected = (this.efForm.controls['type'].value === EMISSION_FACTOR_TYPE.ENERGY)
        this.energyChange()

        if (this.energyTypeSelected) this.efForm.controls['value'].disable()
        else this.efForm.controls['value'].enable()
        ENERGY_INPUTS.forEach(name => this.efForm.controls[name].updateValueAndValidity({emitEvent:false}))
    }

    getHint() {
        return getEmissionFactorDescription(this.efForm.controls['type'].value)
    }

    energyChange() {
        if (this.energyTypeSelected) {
            const {density, energyDensity, energyFactor} = this.efForm.getRawValue()
            const value = Math.round(10000*(energyDensity||0)*(density||0)*(energyFactor||0))/10000
            this.efForm.controls['value'].setValue(value)
        }
    }

    // closes with a clean copy of the factor; the caller saves it
    save() {
        this.efForm.markAllAsTouched()
        if (this.efForm.invalid) return

        const values = this.efForm.getRawValue()
        const energy = values.type === EMISSION_FACTOR_TYPE.ENERGY
        const factor:EmissionFactor = {
            _key: this.data._key,
            name: values.name.trim(),
            description: (values.description || '').trim(),
            scope: values.scope,
            type: values.type,
            value: Number(values.value),
            // the energy inputs only mean something for energy factors
            density: energy ? Number(values.density) : 0,
            energyDensity: energy ? Number(values.energyDensity) : 0,
            energyFactor: energy ? Number(values.energyFactor) : 0,
        }

        // the same checks the server makes, so a problem shows here rather than after saving
        this.saveProblem = validateEmissionFactor(factor, this.existingFactors)
        if (this.saveProblem) return

        this.dialogRef.close(factor);
    }
}
